import { useCallback } from "react";
import { writeFileXLSX, utils } from "xlsx";
import dayjs from "dayjs";

import {
  AdminEventListItem,
  EventSubmissionType,
  translate,
} from "@ascendnow/shared";

import AscLoading from "../AscLoading/AscLoading";
import AscModal from "../AscModal/AscModal";
import AscButton from "../AscButton/AscButton";
import styles from "./AscModalEventSubmissions.module.scss";
import AscTableResponsive from "../AscTableResponsive/AscTableResponsive";

export interface AscModalAddEventPropsI {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  submissions: EventSubmissionType[];
  selectedEvent: AdminEventListItem;
  fetchingSubmissions: boolean;
}

export default function AscModalEventSubmissions({
  visible,
  setVisible,
  submissions,
  selectedEvent,
  fetchingSubmissions,
}: AscModalAddEventPropsI) {
  const exportFile = useCallback(() => {
    const ws = utils.json_to_sheet(
      submissions.map((submission) => ({
        [translate("name")]: submission?.name,
        [translate("signupDate")]: submission?.signupDate
          ? dayjs(submission?.signupDate).format("DD/MM/YYYY")
          : "-",
        [translate("age")]: submission?.age,
        [translate("email")]: submission?.email,
        [translate("school")]: submission?.school,
        [translate("batch")]: submission?.batch,
      })),
      {
        cellStyles: true,
      }
    );
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "event-submissions");
    writeFileXLSX(
      wb,
      selectedEvent?.name
        ? String(selectedEvent.name).trim() + ".xlsx"
        : "event-submissions.xlsx"
    );
  }, [submissions]);

  return (
    <AscModal
      visible={visible}
      setVisible={setVisible}
      modalClassName={styles.modalClassName}
      innerContentClassName={styles.ground}
      withBackground={false}
      header={translate("submissions")}
    >
      <AscLoading
        loading={fetchingSubmissions}
        type="spinner"
        fillWidthAndHeight
        className={styles.spinner}
      >
        <AscTableResponsive<EventSubmissionType>
          rowKey="id"
          columns={[
            {
              title: translate("name"),
              dataIndex: "name",
              style: {
                flex: 1,
              },
              sort: {
                exist: true,
              },
            },
            {
              title: translate("signupDate"),
              dataIndex: "signupDate",
              render: (el) => <>{el ? dayjs(el).format("DD/MM/YYYY") : "-"}</>,
              width: 150,
              align: "center",
              sort: {
                exist: true,
              },
            },
            {
              title: translate("age"),
              dataIndex: "age",
              width: 150,
              align: "center",
              sort: {
                exist: true,
              },
            },
            {
              title: translate("email"),
              dataIndex: "email",
              width: 250,
              sort: {
                exist: true,
              },
            },
            {
              title: translate("school"),
              dataIndex: "school",
              width: 200,
              align: "center",
              sort: {
                exist: true,
              },
              render: (el) => (el ? el : "-"),
            },
            {
              title: translate("batch"),
              dataIndex: "batch",
              width: 200,
              align: "center",
              sort: {
                exist: true,
              },
              render: (el) => (el ? el : "-"),
            },
          ]}
          data={submissions}
        />
      </AscLoading>
      {submissions && submissions.length > 0 && (
        <div className={styles.downloadButtonWrapper}>
          <AscButton className={styles.downloadButton} onClick={exportFile}>
            {translate("downloadList")}
          </AscButton>
        </div>
      )}
    </AscModal>
  );
}
